import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ValidatorsProps, Response } from '@ui/src';
import {
  primaryAccountsApi,
  CreatePrimaryValidatorResponse,
} from '../services/primary-accounts';
import { PrimaryAccount, ChartType } from '../../types';

export interface ChartsAccountsState {
  primaryValidation?: ValidatorsProps;
  accounts?: PrimaryAccount[];
  panelIsOpen: boolean;
  typeOptions: { name: string; value: string }[];
  selectedPrimaryAccountIsLoading?: boolean;
  selectedPrimaryAccount?: PrimaryAccount;
}

const initialState: ChartsAccountsState = {
  accounts: undefined,
  panelIsOpen: false,
  typeOptions: [
    {
      name: 'Debit',
      value: ChartType.DEBIT,
    },
    {
      name: 'Credit',
      value: ChartType.CREDIT,
    },
  ],
};

export const chartAccountsSlice = createSlice({
  name: 'chartAccounts',
  initialState,
  reducers: {
    changePanelState: (state, action: PayloadAction<boolean>) => {
      state.panelIsOpen = action.payload;
    },
    clearPrimariesAccountsState: (state) => {
      state.accounts = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        primaryAccountsApi.endpoints.getCreatePrimaryValidator.matchFulfilled,
        (state, action: PayloadAction<CreatePrimaryValidatorResponse>) => {
          state.primaryValidation =
            action.payload.data.createAccountPrimarySchema;
        },
      )
      .addMatcher(
        primaryAccountsApi.endpoints.getPrimaryAccounts.matchPending,
        (state) => {
          state.accounts = undefined;
        },
      )
      .addMatcher(
        primaryAccountsApi.endpoints.getPrimaryAccounts.matchFulfilled,
        (state, action: PayloadAction<Response<PrimaryAccount>>) => {
          state.accounts = action.payload.data.edges.map((item) => item.node);
        },
      )

      .addMatcher(
        primaryAccountsApi.endpoints.createPrimaryAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: PrimaryAccount }>) => {
          const nextPrimaries = ([] as PrimaryAccount[]).concat(
            state.accounts as PrimaryAccount[],
          );

          nextPrimaries.push(action.payload.data);

          state.accounts = nextPrimaries;
        },
      )

      .addMatcher(
        primaryAccountsApi.endpoints.getPrimaryAccount.matchPending,
        (state) => {
          state.selectedPrimaryAccountIsLoading = true;
        },
      )
      .addMatcher(
        primaryAccountsApi.endpoints.getPrimaryAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: PrimaryAccount }>) => {
          state.selectedPrimaryAccount = action.payload.data;
          state.selectedPrimaryAccountIsLoading = false;
        },
      )
      .addMatcher(
        primaryAccountsApi.endpoints.getPrimaryAccount.matchRejected,
        (state) => {
          state.selectedPrimaryAccountIsLoading = false;
        },
      )

      .addMatcher(
        primaryAccountsApi.endpoints.updatePrimaryAccount.matchFulfilled,
        (state, action: PayloadAction<{ data: PrimaryAccount }>) => {
          const nextPrimaries = state.accounts?.map((item) => {
            let result = { ...item };

            if (item.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.accounts = nextPrimaries;
        },
      );
  },
});

export const { changePanelState, clearPrimariesAccountsState } =
  chartAccountsSlice.actions;

export default chartAccountsSlice.reducer;
